import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { TiltSlab } from "./TiltSlab";
import { STORE_URL } from "../../data/catalog";
import { scrollToId, ease } from "../../lib/scroll";

const lines = [
  { t: "Graded.", c: "" },
  { t: "Slabbed.", c: "text-outline" },
  { t: "Instant.", c: "text-volt" },
];

const Fade = ({ d, children, className }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: d }} className={className}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFront = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 pb-20 lg:pt-32" data-testid="hero-section">
      <div className="spotlight pointer-events-none absolute right-[-10%] top-[-10%] h-[70vh] w-[70vh] rounded-full" />
      <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <Fade d={0.2} className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-cyanx">
            <span className="h-px w-10 bg-cyanx" /> ACE Pokemon slabs · graded &amp; ready to ship
          </Fade>
          <h1 className="font-display text-[3.9rem] font-black uppercase leading-[0.86] tracking-[-0.03em] sm:text-8xl lg:text-[8.6rem]" data-testid="hero-title">
            {lines.map((l, i) => (
              <span key={l.t} className="block overflow-hidden pb-[0.04em]">
                <motion.span className={`block ${l.c}`} initial={{ y: "108%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease, delay: 0.25 + i * 0.13 }}>
                  {l.t}
                </motion.span>
              </span>
            ))}
          </h1>
          <Fade d={0.8} className="mt-8 max-w-md text-base leading-relaxed text-slate-300">
            Browse our hand selected catalog of ACE graded Pokémon slabs, ranging from pristine Gem Mint 10s to historic vintage promos. Every card is authentic, individually photographed, and shipped globally with tracked delivery.
          </Fade>
          <Fade d={0.95} className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("#shop")} className="btn-primary group" data-testid="hero-shop-btn">
              Shop For Slabs <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost group" data-testid="hero-ebay-btn">
              The Vault <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </a>
          </Fade>
          <Fade d={1.1} className="mt-14 grid max-w-lg grid-cols-3 gap-4 border-t border-white/10 pt-6">
            {[["100%", "Positive feedback"], ["UK", "Tracked shipping"], ["Global", "Worldwide delivery"]].map(([a, b]) => (
              <div key={b} data-testid={`hero-stat-${b.split(" ")[0].toLowerCase()}`}>
                <div className="font-display text-2xl font-extrabold sm:text-3xl">{a}</div>
                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">{b}</div>
              </div>
            ))}
          </Fade>
        </motion.div>

        <div className="relative mx-auto w-[78%] max-w-[420px] lg:col-span-5 lg:w-full">
          <motion.div style={{ y: yBack }} className="absolute -left-[22%] top-[12%] w-[70%] -rotate-[12deg] opacity-60" aria-hidden="true">
            <motion.img initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.4, ease, delay: 0.9 }} src="/slabs/ace6.webp" alt="" className="slab-frame aspect-[2/3] w-full rounded-[22px] object-cover blur-[1px]" />
          </motion.div>
          <motion.div style={{ y: yFront }} className="relative">
            <TiltSlab src="/slabs/ace10.webp" alt="ACE Gem Mint 10 graded Armarouge slab" />
          </motion.div>
          <div className="absolute -right-2 top-6 z-10 rotate-6 rounded-full bg-volt px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-black sm:-right-6" data-testid="hero-badge">
            ACE 10 · Gem Mint
          </div>
        </div>
      </div>
    </section>
  );
};
